// Producer character editor hooks.
//
// Lists every character on a project with rate / pickup / contract surface
// and exposes inline-edit + actor-assignment mutations. Optimistic so the
// "Total project budget" header at the top of the table updates on every
// keystroke without a round-trip flicker.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { CharacterRow, PickupMode } from './producer-types'

export const charactersKey = (projectId: string) => ['characters', projectId] as const

export function useCharacters(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? charactersKey(projectId) : ['characters', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () =>
      api<ListEnvelope<CharacterRow>>(`/api/v1/projects/${projectId}/characters`).then(unwrapItems),
  })
}

export interface UpdateCharacterInput {
  character_id: string
  display_name?: string
  line_rate_usd?: number
  pickup_mode?: PickupMode
  pickup_rate_usd?: number
}

export function useUpdateCharacter(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ character_id, ...body }: UpdateCharacterInput) =>
      api<CharacterRow>(`/api/v1/characters/${character_id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onMutate: async (input) => {
      const key = charactersKey(projectId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<CharacterRow[]>(key)
      if (prev) {
        qc.setQueryData<CharacterRow[]>(
          key,
          prev.map((row) => (row.id === input.character_id ? { ...row, ...input } : row)),
        )
      }
      return { prev }
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(charactersKey(projectId), ctx.prev)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: charactersKey(projectId) })
    },
  })
}

export interface AssignActorInput {
  character_id: string
  /** Existing actor id, or null to unassign. */
  actor_id: string | null
}

export function useAssignActor(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ character_id, actor_id }: AssignActorInput) =>
      api<CharacterRow>(`/api/v1/characters/${character_id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ actor_id }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: charactersKey(projectId) })
    },
  })
}

export interface InviteActorInput {
  character_id: string
  email: string
  display_name: string | null
}

/** "Invite new actor" path. Creates the user + assigns + emails the invite. */
export function useInviteActor(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteActorInput) =>
      api<CharacterRow>(`/api/v1/characters/${input.character_id}/invite`, {
        method: 'POST',
        body: JSON.stringify({ email: input.email, display_name: input.display_name }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: charactersKey(projectId) })
    },
  })
}

/** Bulk-rates input row from CSV import. Cents as integers, no fractional dollars. */
export interface BulkRateRow {
  character_code: string
  line_rate_cents: number
}

export interface BulkRatesResult {
  applied_count: number
  skipped_codes: string[]
}

/**
 * Apply a CSV-imported list of {character_code, line_rate_cents} rows to the
 * project. Tries the planned bulk endpoint first; if it 404s, fans out to
 * per-row PATCH /characters/:id calls so the form still works pre-API.
 *
 * TODO(api): once `/projects/:id/characters/rates/bulk` ships, drop the
 * per-row fan-out and trust the server transaction.
 */
export function useBulkUpdateCharacterRates(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: BulkRateRow[]): Promise<BulkRatesResult> => {
      try {
        return await api<BulkRatesResult>(`/api/v1/projects/${projectId}/characters/rates/bulk`, {
          method: 'POST',
          body: JSON.stringify({ rows }),
        })
      } catch {
        // Fallback: lookup characters and PATCH each one whose code matches.
        const characters = await api<ListEnvelope<CharacterRow>>(
          `/api/v1/projects/${projectId}/characters`,
        ).then(unwrapItems)
        const byCode = new Map(characters.map((c) => [c.character_code, c.id]))
        const skipped: string[] = []
        let applied = 0
        for (const row of rows) {
          const id = byCode.get(row.character_code)
          if (!id) {
            skipped.push(row.character_code)
            continue
          }
          await api<CharacterRow>(`/api/v1/characters/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ line_rate_usd: row.line_rate_cents / 100 }),
          })
          applied += 1
        }
        return { applied_count: applied, skipped_codes: skipped }
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: charactersKey(projectId) })
    },
  })
}

/** Pure: total project budget given a list of rate/line rows. */
export function totalBudgetUsd(characters: CharacterRow[]): number {
  let total = 0
  for (const c of characters) {
    total += c.line_rate_usd * c.line_count
    if (c.pickup_mode === 'per_pickup') {
      // Allow up to ~5% of lines as pickups in the projection.
      total += c.pickup_rate_usd * Math.ceil(c.line_count * 0.05)
    }
  }
  return total
}
